import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleLineItemClick } from "../utils/lineItemNavigation";

function VerticalCompaniesTable({ data }) {
  const companies = data.map((c) => c.name);
  const [expandedMap, setExpandedMap] = useState({});
  const navigate = useNavigate();

  const toggle = (key) =>
    setExpandedMap((prev) => ({
      ...prev,
      [key]: prev[key] === false, // undefined → false on first click
    }));

  const getNodeAmount = (node) => {
    if (!node) return null;
    return node.displayAmount ?? null;
  };

  const sumChildren = (node, companyIndex) => {
    if (!node?.children) return 0;
    return node.children.reduce((sum, ch) => {
      const childNode = companyIndex.get(ch.__key);
      return (
        sum +
        (getNodeAmount(childNode) || 0) +
        sumChildren(ch, companyIndex)
      );
    }, 0);
  };

  const formatCurrency = (value, showDash = true) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (isNaN(num)) return "";
    if (num === 0 && showDash) return "–";
    if (num === 0 && !showDash) return "";
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  /* index companies by path */
  const indexCompany = (company) => {
    const map = new Map();

    const walk = (node, path = []) => {
      const key = [...path, node.name].join("|||");
      map.set(key, { ...node, __key: key });
      (node.children || []).forEach((ch) => walk(ch, [...path, node.name]));
    };

    (company.children || []).forEach((root) => walk(root));
    return map;
  };

  const companyIndexes = Object.fromEntries(
    data.map((c) => [c.name, indexCompany(c)])
  );

  /* flatten row structure */
  const rows = [];

  const walkShape = (node, level = 0, path = []) => {
    const key = [...path, node.name].join("|||");

    rows.push({
      key,
      name: node.name,
      level,
      hasChildren: node.children?.length > 0,
      ancestorKeys: path.map((_, i) =>
        path.slice(0, i + 1).join("|||")
      ),
    });

    (node.children || []).forEach((ch) =>
      walkShape(ch, level + 1, [...path, node.name])
    );
  };

  (data[0]?.children || []).forEach((root) => walkShape(root));

  const isVisible = (row) =>
    row.ancestorKeys.every(
      (ancestorKey) => expandedMap[ancestorKey] !== false
    );

  /* render */
  return (
    <div className="report-table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th>Name</th>
            {companies.map((c) => (
              <th key={c}>{c}</th>
            ))}
            <th>Total</th>
          </tr>
        </thead>

        <tbody>
          {rows.filter(isVisible).flatMap((row, i, visibleRows) => {
            const expanded = expandedMap[row.key] !== false;

            const cellValues = companies.map((company) => {
              const idx = companyIndexes[company];
              const node = idx.get(row.key);

              if (!node) return "";

              // Parent row logic
              if (row.hasChildren) {
                if (expanded) {
                  // EXACT match to ReportRow: no numbers, no dashes
                  return "";
                }
                // collapsed → show total
                return (
                  getNodeAmount(node) +
                  sumChildren(node, idx)
                );
              }

              // Leaf
              return getNodeAmount(node);
            });

            const total =
              expanded && row.hasChildren
                ? ""
                : cellValues.reduce(
                    (sum, v) => sum + (Number(v) || 0),
                    0
                  );

            const mainRow = (
              <tr key={row.key}>
                <td style={{ paddingLeft: `${row.level * 20}px` }}>
                  {row.hasChildren && (
                    <span
                      onClick={() => toggle(row.key)}
                      style={{
                        cursor: "pointer",
                        display: "inline-block",
                        marginRight: 4,
                        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.15s ease",
                      }}
                    >
                      ▶
                    </span>
                  )}
                  {row.name}
                </td>

                {cellValues.map((v, i) => {
                  const isLeaf = !row.hasChildren;
                  const company = companies[i];
                  const node = companyIndexes[company]?.get(row.key);

                  return (
                    <td
                      key={i}
                      style={{
                        textAlign: "right",
                        cursor: isLeaf ? "pointer" : "default",
                      }}
                      className={isLeaf ? "clickable" : ""}
                      onClick={() => {
                        if (!isLeaf || !node) return;

                        handleLineItemClick({
                          quickbooksItemId: node.quickbooksItemId,
                          companyId: node.companyId,
                          amount: v,
                          navigate,
                        });
                      }}
                    >
                      {formatCurrency(v, !row.hasChildren || !expanded)}
                    </td>
                  );
                })}

                <td
                  className="total-cell"
                  style={{ textAlign: "right", fontWeight: "bold" }}
                >
                  {formatCurrency(total, true)}
                </td>
              </tr>
            );

            const nextRow = visibleRows[i + 1];
            const nextLevel = nextRow ? nextRow.level : -1;

            // start with the normal row
            const outputRows = [mainRow];

            // figure out which ancestor sections are closing after this row
            const closingAncestorKeys =
              row.ancestorKeys?.slice(nextLevel >= 0 ? nextLevel : 0).reverse() || [];

            // if this row is a leaf, or a collapsed parent, it may end one or more ancestor sections
            closingAncestorKeys.forEach((ancestorKey) => {
              if (expandedMap[ancestorKey] === false) return;

              const ancestorRow = rows.find((r) => r.key === ancestorKey);
              if (!ancestorRow) return;

              const subtotalCells = companies.map((company) => {
                const idx = companyIndexes[company];
                const node = idx.get(ancestorKey);

                if (!node) return "";

                return (getNodeAmount(node) || 0) + sumChildren(node, idx);
              });

              const subtotalTotal = subtotalCells.reduce(
                (sum, v) => sum + (Number(v) || 0),
                0
              );

              outputRows.push(
                <tr key={`${ancestorKey}-total`} className="total-row">
                  <td
                    style={{
                      paddingLeft: `${(ancestorRow.level + 1) * 20}px`,
                      fontWeight: "bold",
                    }}
                  >
                    Total {ancestorRow.name}:
                  </td>

                  {subtotalCells.map((v, i) => (
                    <td key={i} style={{ textAlign: "right", fontWeight: "bold" }}>
                      {formatCurrency(v, true)}
                    </td>
                  ))}

                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    {formatCurrency(subtotalTotal, true)}
                  </td>
                </tr>
              );
            });

            return outputRows;
          })}
        </tbody>
      </table>
    </div>
  );
}

export default VerticalCompaniesTable;
